import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Minimize2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { sendMessageToGemini } from "../../services/chatService";

// 44x44 hit area for the header's icon controls (HIG minimum).
const headerIconButton =
    "inline-flex items-center justify-center h-11 w-11 rounded-full hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60";

export default function ChatWidget() {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { id: 1, text: "Hi! I'm Hedi's AI assistant. Ask me anything about his skills, projects, or experience!", sender: "bot" }
    ]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = { id: Date.now(), text: input, sender: "user" };
        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);

        const history = messages.slice(1);
        const botResponseText = await sendMessageToGemini(userMessage.text, history);

        const botMessage = { id: Date.now() + 1, text: botResponseText, sender: "bot" };
        setMessages(prev => [...prev, botMessage]);
        setIsLoading(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                className="fixed bottom-20 right-4 sm:bottom-24 sm:right-8 z-50 h-14 w-14 rounded-full bg-accent hover:bg-accent-hover text-white shadow-lg transition-all duration-300 hover:scale-105 active:scale-95 flex items-center justify-center group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
                aria-label="Open chat"
            >
                <MessageCircle className="w-6 h-6" />
                <span className="absolute right-full mr-4 bg-label text-surface text-xs px-2 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    Chat with AI
                </span>
            </button>
        );
    }

    if (isMinimized) {
        return (
            <div
                className="fixed bottom-20 right-2 sm:bottom-24 sm:right-8 z-50 glass rounded-2xl w-[calc(100vw-1rem)] max-w-[18rem] sm:w-72 flex items-center justify-between pl-4 pr-2 py-2 cursor-pointer"
                onClick={() => setIsMinimized(false)}
            >
                <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="font-semibold text-label">Hedi's Assistant</span>
                </div>
                <button
                    onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}
                    className="inline-flex items-center justify-center h-11 w-11 rounded-full text-label-secondary hover:text-label hover:bg-black/[0.05] dark:hover:bg-white/[0.08] transition-colors"
                    aria-label="Close chat"
                >
                    <X className="w-5 h-5" />
                </button>
            </div>
        );
    }

    return (
        <div className="fixed bottom-20 right-2 sm:bottom-24 sm:right-4 md:right-8 z-50 w-[calc(100vw-1rem)] max-w-sm sm:max-w-none sm:w-96 h-[70vh] sm:h-[500px] max-h-[80vh] flex flex-col bg-surface-elevated border border-separator rounded-3xl shadow-glass overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-3 pl-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                <div className="flex items-center gap-3">
                    <div className="bg-white/20 p-2 rounded-full">
                        <MessageCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-sm">Hedi's Assistant</h3>
                        <span className="text-xs text-blue-100 flex items-center">
                            <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5" />
                            Online
                        </span>
                    </div>
                </div>
                <div className="flex items-center">
                    <button
                        onClick={() => setIsMinimized(true)}
                        className={headerIconButton}
                        aria-label="Minimize chat"
                    >
                        <Minimize2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setIsOpen(false)}
                        className={headerIconButton}
                        aria-label="Close chat"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-bg scrollbar-hide">
                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
                    >
                        <div
                            className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-[15px] leading-relaxed ${msg.sender === "user"
                                ? "bg-accent text-white rounded-br-md"
                                : "bg-surface-elevated text-label border border-separator rounded-bl-md shadow-card"
                                }`}
                        >
                            {msg.text}
                        </div>
                    </div>
                ))}
                {isLoading && (
                    <div className="flex justify-start">
                        <div className="bg-surface-elevated p-3 rounded-2xl rounded-bl-md border border-separator shadow-card">
                            <Loader2 className="w-5 h-5 animate-spin text-accent-text" />
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-3 bg-surface-elevated border-t border-separator">
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder="Ask about my projects..."
                        className="flex-1 h-11 px-3.5 text-[15px] bg-black/[0.04] dark:bg-white/[0.06] border border-separator rounded-full focus:outline-none focus:ring-2 focus:ring-accent text-label placeholder-label-tertiary transition-all"
                        disabled={isLoading}
                    />
                    <Button
                        onClick={handleSend}
                        className={`h-11 w-11 p-0 shrink-0 ${isLoading || !input.trim() ? "opacity-40 cursor-not-allowed" : ""}`}
                        disabled={isLoading || !input.trim()}
                        aria-label="Send message"
                    >
                        <Send className="w-[18px] h-[18px]" />
                    </Button>
                </div>
                <div className="mt-2 text-center text-[11px] text-label-tertiary">
                    Powered by Gemini AI • Responses may be generated
                </div>
            </div>
        </div>
    );
}
